import React, { useEffect, useMemo, useState } from "react";
import { animate, stagger } from "motion";
import Card from "./Card";
import ScratchOff from "./ScratchOff";

const reasonsList: string[] = [
  "I love your smile, your beauty and your presence",
  "You cheer me on in everything I do",
  "You inspire me to be my best self",
  "I love that we are walking on a path towards Jesus",
  "I love our many many many food dates - and many more to come",
  "I love that you want to marry me and be with me (p)orever",
  "I appreciate that despite my flaws, you have stuck by my side",
  "I promise to always aim to be a better man for you and myself",
  "I will forever be patient with you, as I know you will be with me too",
  "I thank you for the compromise you have given me",
  "I love all you, every bit of you - even when you don't love yourself",
  "Happy valentines my love"
];

const App: React.FC = () => {
  const [flipped, setFlipped] = useState<boolean[]>(
    Array.from({ length: reasonsList.length }, () => false)
  );
  const [showCelebration, setShowCelebration] = useState(false);
  const [showScratch, setShowScratch] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showCards, setShowCards] = useState(false);
  const [typed, setTyped] = useState("");

  const introText =
    "I wanted you to feel how much you are loved. Take a moment, then tap next to reveal each reason, one by one.";

  const allRevealed = useMemo(() => flipped.every(Boolean), [flipped]);

  useEffect(() => {
    if (!allRevealed) {
      setShowCelebration(false);
      setShowScratch(false);
      setShowGrid(true);
      return;
    }
    setShowCelebration(false);
    setShowScratch(false);
    const timer = window.setTimeout(() => {
      setShowCelebration(true);
      setShowScratch(true);
      setShowGrid(false);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [allRevealed]);

  useEffect(() => {
    if (!showCards || !showGrid) return;
    const cards = document.querySelectorAll(".card");
    if (cards.length === 0) return;
    animate(
      cards,
      { opacity: [0, 1], transform: ["translateY(18px) scale(0.98)", "translateY(0) scale(1)"] },
      { duration: 0.6, delay: stagger(0.06), easing: "ease-out" }
    );
  }, [showCards, showGrid]);

  useEffect(() => {
    if (showCards) return;
    setTyped("");
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setTyped(introText.slice(0, index));
      if (index >= introText.length) {
        window.clearInterval(timer);
      }
    }, 60);
    return () => window.clearInterval(timer);
  }, [showCards, introText]);

  const playFlipSound = () => {
    const AudioCtx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    const startTime = ctx.currentTime;
    const step = 0.07;
    const duration = 0.22;

    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(0.06, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.5);

    filter.type = "highpass";
    filter.frequency.setValueAtTime(700, startTime);
    filter.Q.value = 0.8;

    filter.connect(gain);
    gain.connect(ctx.destination);

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = i % 2 === 0 ? "sine" : "triangle";
      const noteStart = startTime + i * step;
      osc.frequency.setValueAtTime(freq, noteStart);
      osc.connect(filter);
      osc.start(noteStart);
      osc.stop(noteStart + duration);
      if (i === notes.length - 1) {
        osc.onended = () => ctx.close();
      }
    });
  };

  const handleFlip = (index: number) => {
    playFlipSound();
    setFlipped((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  return (
    <div className="page">
      <header className="hero">
        <p className="eyebrow">To My Valentine Dazzlyn...</p>
        <h1>Reasons You Are Loved</h1>
      </header>

      {!showCards && (
        <section className="intro">
          <p className="typewriter">{typed}</p>
          <button className="next-btn" type="button" onClick={() => setShowCards(true)}>
            Next...
          </button>
        </section>
      )}

      {showCards && showGrid && (
        <main className="grid" role="grid">
          {reasonsList.map((reason, index) => (
            <Card
              key={`${reason}-${index}`}
              reason={reason}
              isFlipped={flipped[index]}
              onFlip={() => handleFlip(index)}
            />
          ))}
        </main>
      )}

      {allRevealed && (
        <div className="reveal-area">
          <div className="reveal-banner" role="status">
            All reasons revealed!
          </div>
          {showCelebration && <div className="confetti" aria-hidden="true" />}
          {showScratch && (
            <ScratchOff
              title="One Secret Gift"
              body="This coupon is redeemable for a special gift picked just for you."
              footer="Gift to be used when you want to feel extra loved."
            />
          )}
        </div>
      )}
    </div>
  );
};

export default App;
